import { getMemoOptions, memo, getGroupedRowModel, RowData, RowModel, Table } from '@tanstack/react-table';
import { TableOptions } from '@/types/table';

const isNotPlaceholder = () => false;

export function makeGetCustomGroupedRowModel<TData extends RowData>(
  shouldFlattenGroup: TableOptions<TData>['shouldFlattenGroup'],
): () => (table: Table<TData>) => () => RowModel<TData> {
  return () => {
    return table => {
      return memo(
        () => [table.getState().grouping, table.getPreGroupedRowModel()],
        (grouping, rowModel) => {
          const groupedRowModel = getGroupedRowModel<TData>()(table)();
          try {
            if (!rowModel.rows.length || !grouping.length) {
              return groupedRowModel;
            }
            const precisedModel: typeof groupedRowModel = {
              rows: [],
              flatRows: [],
              rowsById: {},
            };
            for (let i = 0; i < groupedRowModel.rows.length; i++) {
              const row = groupedRowModel.rows[i];
              if (!shouldFlattenGroup(row)) {
                precisedModel.rows.push(row);
                continue;
              }
              row.subRows.forEach(subRow => {
                subRow.depth = row.depth;
                subRow.id = `${row.id}_${subRow.id}`;
                // Rollback defined isPlaceholder function for group-level cells of flatten rows
                subRow.getAllCells().forEach(cell => {
                  if (grouping.find(group => group === cell.column.id)) {
                    cell.getIsPlaceholder = isNotPlaceholder;
                  }
                });
                precisedModel.rows.push(subRow);
              });
            }
            for (let i = 0; i < precisedModel.rows.length; i++) {
              const row = precisedModel.rows[i];
              precisedModel.flatRows.push(row);
              precisedModel.rowsById[row.id] = row;
              if (row.subRows.length > 0) {
                precisedModel.flatRows.push(...row.subRows);
                row.subRows.forEach(subRow => {
                  precisedModel.rowsById[subRow.id] = subRow;
                });
              }
            }
            return precisedModel;
          } catch (error) {
            return groupedRowModel;
          }
        },
        getMemoOptions(table.options, 'debugTable', 'getGroupedRowModel', () => {
          table._queue(() => {
            table._autoResetExpanded();
            table._autoResetPageIndex();
          });
        }),
      );
    };
  };
}
